// src/pages/Profits/ViewProfits.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Toolbar,
  InputBase,
  ButtonGroup,
  TablePagination,
  Grid,
  Divider,
  Stack,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import {
  getAllProfits,
  revokeProfit,
  getProfitById,
} from "../../services/api";

/**
 * Admin "View Profits" page.
 * - Calls getAllProfits(page, limit) from services/api
 * - Allows searching, simple filtering, pagination
 * - View profit details and revoke (delete) a profit
 *
 * Note: to compute "% of deposit", the backend should include the deposit relation
 * on the profit object (profit.deposit.amount). If not present the column will show "—".
 */

const safe = (v, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : v;

const statusColor = (s) => {
  const st = (s || "").toString().toLowerCase();
  if (st.includes("approved")) return "success";
  if (st.includes("rejected")) return "error";
  return "warning";
};

export default function ViewProfits() {
  const [profits, setProfits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [page, setPage] = useState(0); // zero-based page for UI
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [total, setTotal] = useState(0);

  const [query, setQuery] = useState("");
  const [filterBy, setFilterBy] = useState("all"); // reserved for future filters

  // viewing / revoke
  const [selected, setSelected] = useState(null); // profit object
  const [confirmRevoke, setConfirmRevoke] = useState(null); // profit id
  const [actionBusy, setActionBusy] = useState(false);

  // load is stable: accepts explicit pageIndex and limit so we don't rely on outer vars
  const load = useCallback(async (pageIndex = 1, limit = 20) => {
    setLoading(true);
    setErrorMsg("");
    try {
      // backend expects page starting at 1
      const res = await getAllProfits(pageIndex, limit);
      const body = res?.data ?? {};
      const data = body?.data ?? body ?? [];
      setProfits(Array.isArray(data) ? data : []);
      if (body?.pagination?.total != null) {
        setTotal(Number(body.pagination.total));
      } else if (Array.isArray(data)) {
        setTotal(data.length);
      } else {
        setTotal(0);
      }
    } catch (err) {
      console.error("Failed to load profits", err);
      setErrorMsg(err?.response?.data?.message || err?.message || "Failed to load profits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // load first page using current rowsPerPage
    load(1, rowsPerPage);
  }, [rowsPerPage, load]);

  const refresh = () => {
    load(page + 1, rowsPerPage);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    load(newPage + 1, rowsPerPage);
  };

  const handleRowsPerPageChange = (e) => {
    const lp = parseInt(e.target.value, 10) || 20;
    setRowsPerPage(lp);
    setPage(0);
    load(1, lp);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profits;
    return profits.filter((p) => {
      const user = p.user || {};
      const admin = p.admin || {};
      const txt = [
        p.id,
        p.depositId,
        p.amount,
        p.note,
        user.name,
        user.email,
        user.rockstarId,
        admin.name,
        admin.email,
      ]
        .filter(Boolean)
        .join(" ")
        .toString()
        .toLowerCase();
      return txt.includes(q);
    });
  }, [profits, query]);

  const visible = filtered.slice(0, rowsPerPage); // because server does pagination, we just show filtered result for current fetch

  // view details
  const openDetails = async (pid) => {
    try {
      const res = await getProfitById(pid);
      const body = res?.data ?? res;
      const data = body?.data ?? body;
      setSelected(data);
    } catch (err) {
      console.error("Failed to load profit details", err);
      setErrorMsg(err?.response?.data?.message || err?.message || "Failed to load details");
    }
  };
  const closeDetails = () => setSelected(null);

  // revoke profit
  const startRevoke = (profitId) => setConfirmRevoke(profitId);
  const cancelRevoke = () => setConfirmRevoke(null);

  const doRevoke = async () => {
    if (!confirmRevoke) return;
    setActionBusy(true);
    try {
      await revokeProfit(confirmRevoke);
      setConfirmRevoke(null);
      // reload current page
      load(page + 1, rowsPerPage);
    } catch (err) {
      console.error("Failed to revoke profit", err);
      setErrorMsg(err?.response?.data?.message || err?.message || "Failed to revoke");
    } finally {
      setActionBusy(false);
    }
  };

  // compute percent of deposit
  const profitPercent = (p) => {
    if (!p) return "—";
    const pAmt = typeof p.amount === "string" ? Number(p.amount) : p.amount;
    const dAmt = p.deposit && p.deposit.amount ? (typeof p.deposit.amount === "string" ? Number(p.deposit.amount) : p.deposit.amount) : null;
    if (!dAmt || !Number.isFinite(dAmt) || dAmt === 0) return "—";
    const pct = (Number(pAmt) / Number(dAmt)) * 100;
    return `${pct.toFixed(2)}%`;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Profits (Admin)
      </Typography>

      <Paper sx={{ mb: 2, p: 1 }}>
        <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
          <Paper
            component="form"
            sx={{
              p: "2px 8px",
              display: "flex",
              alignItems: "center",
              width: 420,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.08)",
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <SearchIcon color="action" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search id, user, email, rockstar id, note..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </Paper>

          <ButtonGroup variant="outlined" size="small">
            <Button color={filterBy === "all" ? "primary" : "inherit"} onClick={() => setFilterBy("all")}>
              All
            </Button>
            <Button color={filterBy === "recent" ? "primary" : "inherit"} onClick={() => setFilterBy("recent")}>
              Recent
            </Button>
          </ButtonGroup>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Refresh">
            <IconButton onClick={refresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </Paper>

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box sx={{ p: 2 }}>
                <Typography color="error">{errorMsg}</Typography>
              </Box>
            )}

            <TableContainer sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Rockstar ID</TableCell>
                    <TableCell>Profit</TableCell>
                    <TableCell>Deposit ID</TableCell>
                    <TableCell>Deposit Amount</TableCell>
                    <TableCell>Profit %</TableCell>
                    <TableCell>Note</TableCell>
                    <TableCell>Added By</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visible.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                        No profits found
                      </TableCell>
                    </TableRow>
                  ) : (
                    visible.map((p, idx) => {
                      const user = p.user || {};
                      const admin = p.admin || {};
                      const deposit = p.deposit || {};
                      return (
                        <TableRow key={p.id ?? idx} hover>
                          <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                          <TableCell>
                            <Stack>
                              <Typography sx={{ fontWeight: 700 }}>{safe(user.name || user.email)}</Typography>
                              <Typography variant="caption" color="text.secondary">{safe(user.email)}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{safe(user.rockstarId)}</TableCell>
                          <TableCell>{safe(p.amount)}</TableCell>
                          <TableCell>{safe(p.depositId ?? deposit.id)}</TableCell>
                          <TableCell>{deposit.amount ?? "—"}</TableCell>
                          <TableCell>{profitPercent(p)}</TableCell>
                          <TableCell>{safe(p.note)}</TableCell>
                          <TableCell>{safe(admin.name || admin.email)}</TableCell>
                          <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString() : "—"}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="Details">
                              <IconButton size="small" onClick={() => openDetails(p.id)}>
                                <VisibilityOutlinedIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Revoke">
                              <IconButton
                                size="small"
                                onClick={() => startRevoke(p.id)}
                                color="error"
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* Details dialog */}
      <Dialog open={Boolean(selected)} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>Profit Details</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">User</Typography>
                  <Typography>{safe(selected.user?.name || selected.user?.email)}</Typography>
                  <Typography variant="caption" color="text.secondary">{safe(selected.user?.email)}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Rockstar ID</Typography>
                  <Typography>{safe(selected.user?.rockstarId)}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Profit Amount</Typography>
                  <Typography>{safe(selected.amount)}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2">Deposit</Typography>
                  <Typography>{safe(selected.depositId || selected.deposit?.id)}</Typography>
                  <Typography variant="caption" color="text.secondary">{selected.deposit?.amount ? `Deposit Amount: ${selected.deposit.amount}` : ""}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Note</Typography>
                  <Typography>{safe(selected.note)}</Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={Boolean(confirmRevoke)} onClose={cancelRevoke}>
        <DialogTitle>Revoke Profit</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to revoke this profit? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRevoke} disabled={actionBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doRevoke} disabled={actionBusy}>
            {actionBusy ? "Revoking..." : "Revoke"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
